/**
 * Network printer adapter — wraps the existing QZ Tray + ESC/POS flow.
 *
 * Contract implemented (see printerService.js for the shape all adapters
 * must satisfy):
 *   - mode              : 'network'
 *   - isAvailable()     : Promise<boolean>
 *   - getStatus()       : Promise<{available, deviceName, error?}>
 *   - printReceipt(order, opts?) : Promise<{success, error?}>
 *   - testPrint(opts?)  : Promise<{success, error?}>
 *
 * This adapter is a THIN wrapper. It does not re-implement any receipt
 * formatting — it delegates to /app/frontend/src/utils/qzPrinter.js which
 * has been in production since day one.
 */

import {
  isQZConnected,
  printReceipt as qzPrintReceipt,
  testPrint as qzTestPrint,
  getSavedPrinterName,
} from '../../utils/qzPrinter';

export const networkPrinterAdapter = {
  mode: 'network',

  async isAvailable() {
    // Non-invasive probe — we do NOT auto-connect QZ Tray here because
    // this is called from status checks and auto-print gates. Forcing a
    // websocket connect on a probe would surprise operators who have
    // deliberately disconnected QZ. Callers that need to actually print
    // handle the connect explicitly (see qzPrinter.printReceipt).
    return isQZConnected() && !!getSavedPrinterName();
  },

  async getStatus() {
    const available = await this.isAvailable();
    return {
      available,
      deviceName: getSavedPrinterName() || null,
      error: available ? null : 'QZ Tray not connected or no printer selected',
    };
  },

  async printReceipt(order, opts = {}) {
    const printerName = opts.printerName || getSavedPrinterName();
    if (!printerName) {
      return { success: false, error: 'No printer selected. Configure one in Printer Settings.' };
    }
    try {
      await qzPrintReceipt(order, printerName);
      return { success: true };
    } catch (err) {
      return { success: false, error: String(err?.message || err).slice(0, 300) };
    }
  },

  async testPrint(opts = {}) {
    const printerName = opts.printerName || getSavedPrinterName();
    if (!printerName) {
      return { success: false, error: 'No printer selected. Configure one in Printer Settings.' };
    }
    try {
      await qzTestPrint(printerName);
      return { success: true };
    } catch (err) {
      return { success: false, error: String(err?.message || err).slice(0, 300) };
    }
  },
};

export default networkPrinterAdapter;
